import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KG_CO2_SAVED_PER_KM = 0.7; // diesel HGV 0.8 vs renewable-charged EV 0.1
const KM_PER_LITRE_DIESEL = 2.5;
const KG_CO2_PER_TREE_YEAR = 21;
const COST_SAVED_PER_KM = 22; // diesel ₹36/km vs electric ₹14/km

const formatIN = (n) => Math.round(n).toLocaleString("en-IN");

const Result = ({ testid, value, unit, label }) => (
  <div className="border-l-2 border-volt/70 pl-5">
    <div className="flex items-baseline gap-2">
      <motion.span
        key={value}
        initial={{ opacity: 0.4, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        data-testid={testid}
        className="font-mono text-4xl font-bold tracking-tight text-white md:text-5xl"
      >
        {formatIN(value)}
      </motion.span>
      <span className="font-mono text-xs uppercase tracking-[0.15em] text-volt">
        {unit}
      </span>
    </div>
    <p className="mt-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-smoke">
      {label}
    </p>
  </div>
);

const Slider = ({ testid, label, min, max, step, value, onChange, format }) => (
  <div>
    <div className="flex items-baseline justify-between">
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
        {label}
      </label>
      <span className="font-mono text-lg font-bold text-volt">{format(value)}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="mt-4 h-1 w-full cursor-pointer appearance-none bg-line accent-volt"
    />
  </div>
);

export default function Calculator() {
  const [monthlyKm, setMonthlyKm] = useState(25000);
  const [trucks, setTrucks] = useState(10);
  const [leadEmail, setLeadEmail] = useState("");
  const [sending, setSending] = useState(false);

  const results = useMemo(() => {
    const annualKm = monthlyKm * 12 * trucks;
    const co2Kg = annualKm * KG_CO2_SAVED_PER_KM;
    return {
      co2Tonnes: co2Kg / 1000,
      dieselLitres: annualKm / KM_PER_LITRE_DIESEL,
      trees: co2Kg / KG_CO2_PER_TREE_YEAR,
      costSaved: annualKm * COST_SAVED_PER_KM,
    };
  }, [monthlyKm, trucks]);

  const sendResults = async (e) => {
    e.preventDefault();
    setSending(true);
    const message = [
      "Impact estimate from the ElectroHaul calculator:",
      `Fleet: ${trucks} trucks x ${formatIN(monthlyKm)} km per month.`,
      `CO2 eliminated: ${formatIN(results.co2Tonnes)} tonnes per year.`,
      `Diesel displaced: ${formatIN(results.dieselLitres)} litres per year.`,
      `Tree equivalent: ${formatIN(results.trees)} trees.`,
      `Fuel-cost advantage: Rs ${formatIN(results.costSaved)} per year.`,
    ].join("\n");
    try {
      await axios.post(`${API}/enquiries`, {
        name: "Calculator Lead",
        email: leadEmail,
        message,
        source: "calculator",
      });
      toast.success("Estimate locked in.", {
        description: "Our team will follow up with your savings breakdown.",
      });
      setLeadEmail("");
    } catch (err) {
      toast.error("Could not send estimate.", {
        description: "Please check your email and try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="border-t border-line py-32"
    >
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <Overline>Impact Calculator</Overline>
          <h2 className="mt-6 font-display text-4xl font-semibold tracking-tighter sm:text-5xl">
            Sustainability, <span className="text-volt">quantified.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-smoke md:text-base">
            Estimate what your freight network saves every year by switching
            from diesel to ElectroHaul's electric fleet.
          </p>

          <div className="mt-12 space-y-10">
            <Slider
              testid="calc-distance-slider"
              label="Monthly distance per truck"
              min={2000}
              max={120000}
              step={1000}
              value={monthlyKm}
              onChange={setMonthlyKm}
              format={(v) => `${formatIN(v)} km`}
            />
            <Slider
              testid="calc-trucks-slider"
              label="Trucks in your network"
              min={1}
              max={200}
              step={1}
              value={trucks}
              onChange={setTrucks}
              format={(v) => `${v}`}
            />
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <div
            data-testid="calc-results"
            className="grid h-full grid-cols-1 content-center gap-y-12 border border-line bg-ash/50 p-8 sm:grid-cols-2 sm:gap-x-10 md:p-12"
          >
            <Result
              testid="calc-co2-result"
              value={results.co2Tonnes}
              unit="tonnes CO₂ / yr"
              label="Emissions eliminated from your supply chain"
            />
            <Result
              testid="calc-diesel-result"
              value={results.dieselLitres}
              unit="litres / yr"
              label="Diesel displaced by electric kilometres"
            />
            <Result
              testid="calc-trees-result"
              value={results.trees}
              unit="trees"
              label="Equivalent tree cover absorbing that carbon"
            />
            <Result
              testid="calc-cost-result"
              value={results.costSaved}
              unit="₹ saved / yr"
              label="Estimated fuel-cost advantage over diesel"
            />
            <p className="font-mono text-[9px] uppercase leading-relaxed tracking-[0.15em] text-smoke/70 sm:col-span-2">
              Illustrative estimates. Assumes diesel HGV at 0.8 kg CO₂/km and 2.5
              km/l at ₹90/l; ElectroHaul EV on renewable-linked charging at 0.1
              kg CO₂/km and ₹14/km; 21 kg CO₂ absorbed per tree per year.
            </p>
            <form
              onSubmit={sendResults}
              data-testid="calc-lead-form"
              className="flex flex-col gap-3 border-t border-line pt-6 sm:col-span-2 sm:flex-row"
            >
              <input
                type="email"
                required
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="calc-email-input"
                className="w-full flex-1 rounded-none border border-line bg-transparent px-4 py-3 text-sm text-white placeholder:text-smoke/60 focus:border-volt focus:outline-none focus:ring-2 focus:ring-volt"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="calc-email-button"
                className="group relative overflow-hidden bg-volt px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-black disabled:opacity-60"
              >
                <span className="absolute inset-0 -translate-x-full bg-cyber transition-transform duration-300 ease-out group-hover:translate-x-0" />
                <span className="relative flex items-center justify-center gap-2">
                  {sending ? (
                    <>
                      Sending <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      Email Me These Results
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </span>
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
